import { Box, Drawer, DrawerBody, DrawerContent, DrawerHeader, Flex, HStack, useMediaQuery } from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { FiCheck } from "react-icons/fi";
import "../../../App.css";
import APButton from "../../../components/APButton";
import { WordItem } from "ap-exerciser-sdk/models";
import { Error, Success } from "../../../components/APAssetAtlas";
import { useVocabIntl } from "../../../hooks/useVocabTranslation";

interface ValidationStatusProps {
  onNext: (value: string) => void;
  open: boolean;
  word?: WordItem;
  currentWord: string;
  status: "success" | "warning" | "fail" | "pass" | "none";
  clearStatus: () => void;
  clearWordValue: () => void;
}

export default function ValidationStatus(props: ValidationStatusProps) {
  const { onNext, open, word, currentWord, status, clearStatus, clearWordValue } = props;
  const { getFormattedString, APFormattedMessage } = useVocabIntl();

  const [closing, setClosing] = useState<boolean>(false);
  const [disableNext, setDisableNext] = useState<boolean>(true);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  useEffect(() => {setTimeout(() => setDisableNext(false), 1)}, []);

  const handleNext = React.useCallback(() => {
    clearWordValue();
    clearStatus();
    setClosing(true);
    setTimeout(() => {
      onNext(status);
      setClosing(false);
    }, 500);
  }, [status, onNext]);

  const formattedWord = word ? word.label : "";

  const title = React.useMemo(
    () => (
      <APFormattedMessage
        id={
          status === "success" ? "exercise-correct-modal-heading" :
            status === "warning" ? "exercise-warning-modal-heading" :
              "exercise-incorrect-modal-heading"
        }
      />
    ), [status, word]
  );

  const description = React.useMemo(
    () => (
      <APFormattedMessage
        id={
          status === "success" ? "exercise-correct-modal-description" :
            status === "warning" ? "exercise-warning-modal-description" :
              "exercise-incorrect-modal-description"
        }
      />
    ), [status, formattedWord]
  );

  const nextButton = useCallback((button: HTMLButtonElement) => button?.focus(), [disableNext]);

  return (
    <Drawer isOpen={open} onClose={handleNext} placement={"bottom"} size="sm" closeOnOverlayClick={false}>
      <DrawerContent>
        <DrawerHeader minHeight={"0.5rem"} bg={status === "success" ? "success" : "alert.fg"} padding={0} />
        <DrawerBody>
          <Flex direction={isLargeScreen ? "row" : "column"} justifyContent={"space-around"} bg={"white"}
                className="ap-dialog-content" gap={"1rem"}>
            <Box boxSize={"96px"}>
              <Success display={status === "success" ? "block" : "none"} />
              <Error display={status !== "success" ? "block" : "none"} />
            </Box>

            <Box sx={{ d: "flex", flexDirection: isLargeScreen ? "row" : "column", alignItems: "center" }}>
              <Box sx={{ d: isLargeScreen ? "block" : "flex", alignItems: "center", flexDirection: "column" }}>
                <div className="ap-dialog-title" style={{ textAlign: "center", margin: isLargeScreen ? 0 : "1rem 0" }}>
                  {title} {description} {status !== "warning" && (
                  <span style={{ whiteSpace: "nowrap" }}>« <b>{formattedWord}</b> ».</span>
                )}
                </div>
                <HStack>
                  {status === "fail" && (
                    <HStack sx={{ mt: 2 }}>
                      <Box
                        className={`ap-dialog-word ${"success"}`}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Box sx={{ color: "primary", mr: 1 }}>
                          <FiCheck />
                        </Box>
                        {formattedWord}
                      </Box>
                    </HStack>
                  )}
                </HStack>
              </Box>
            </Box>
            <APButton ref={nextButton}
                      variant={"solid"}
                      disabled={disableNext}
                      onClick={handleNext}
                      aria-label={getFormattedString('aria-label-next-button')}
            >
              <APFormattedMessage id={status === "warning" ? "modal-next-button-warning" : "modal-next-button"} />
            </APButton>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
